import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApolloClient } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import type { DocumentNode } from 'graphql'
import { describeCostControlError } from '../lib/errors'

export type Vars = Record<string, unknown>
export type Mutate = (options: { variables: Vars }) => Promise<{ data?: any } | void>
export interface Reload {
  query: DocumentNode
  variables: Vars
  /** Told when this reload fails, also after a refused write: Apollo 2.6 does not hand that error to the screen's own query. */
  onError?: (error: unknown) => void
}
/** `reloaded`: every reload after the write succeeded, so the screen shows what the write left. */
export type WriteResult<T = any> = { ok: true; data: T; reloaded: boolean } | { ok: false }

/** Mutation options for every Cost Control write: the module shows its own translated errors. */
export const WRITE = { context: { handlesErrors: true } }

/**
 * One queue per screen. Each write runs after the previous one settled, then reloads the given queries network-only.
 * This replaces `refetchQueries` + `awaitRefetchQueries`: in Apollo 2.6 a failed awaited refetch leaves the mutation
 * unsettled forever, and a deduplicated refetch can join one that read the data before the write. A failed reload
 * keeps the write a success and says so. A refused write shows its error and reloads silently: the refusal often
 * means someone else changed the record, and the screen should show what is there now. One failed write never
 * blocks the ones queued behind it.
 */
export function useCostWriteQueue() {
  const { t } = useTranslation()
  const client = useApolloClient()
  const [pending, setPending] = useState(0)
  const queue = useRef<Promise<unknown>>(Promise.resolve())

  /** Every reload settles; resolves to the errors of those that failed. */
  const reload = useCallback((reloads: Reload[]): Promise<unknown[]> => Promise.all(reloads.map((r) =>
    client.query({ query: r.query, variables: r.variables, fetchPolicy: 'network-only', context: { handlesErrors: true } })
      .then(() => null, (e: unknown) => {
        r.onError?.(e)
        return e
      }),
  )).then((results) => results.filter((e) => e !== null)), [client])

  /** `afterRefusal`: what to reload when the write is refused, when that differs from `reloads` (a delete). */
  const run = useCallback(<T = any>(mutate: Mutate, variables: Vars, reloads: Reload[], afterRefusal: Reload[] = reloads): Promise<WriteResult<T>> => {
    setPending((n) => n + 1)
    const unit = queue.current.then(async (): Promise<WriteResult<T>> => {
      try {
        let data: T
        try {
          const res = await mutate({ variables })
          data = (res ? res.data : undefined) as T
        } catch (e: any) {
          toastr.error(t('Common_error'), describeCostControlError(e?.message, t))
          await reload(afterRefusal)
          return { ok: false }
        }
        const failed = await reload(reloads)
        if (failed.length) {
          console.warn('Failed to reload Cost Control data after a write', failed[0])
          toastr.warning(t('Common_error'), t('CostControl_error_reload'))
        }
        return { ok: true, data, reloaded: !failed.length }
      } finally {
        setPending((n) => n - 1)
      }
    })
    queue.current = unit.catch(() => undefined)
    return unit
  }, [t, reload])

  return { saving: pending > 0, run }
}
